/**
 * In-body links from a /compare-insurance/<type>/ wiki page up to its hub.
 *
 * Why this lives in code rather than Contentful: the wiki pages are a CMS
 * content type, but the links they need to carry are an SEO decision about
 * the hub pages, which are code-owned. Keeping the anchors here means they
 * can be reviewed in a PR, and they cannot be lost in a CMS edit.
 *
 * Each entry names an exact phrase in a given section (1-based, matching
 * `questionDesc<n>`). The FIRST occurrence of the phrase in that section is
 * wrapped in a hyperlink; the text itself is never changed. A phrase that is
 * not found (because someone edited the Contentful copy) is skipped silently
 * — the page renders exactly as before, minus that one link.
 */
export const WIKI_HUB_LINKS = {
  'health-insurance': {
    hub: '/health-insurance/',
    callout: {
      lead: 'Our full guide to ',
      anchor: 'health insurance in NZ',
      tail:
        ' covers what private treatment costs, what each layer of cover pays for, how excess and pre-existing conditions work, and how to compare plans with a free adviser.',
    },
    closing: {
      lead: 'For costs, cover and a free adviser comparison, see ',
      anchor: 'compare health insurance NZ',
      tail: '.',
    },
    phrases: [
      { section: 1, phrase: 'Health insurance in New Zealand' },
      { section: 4, phrase: 'cost of health insurance' },
      { section: 5, phrase: 'compare your health insurance' },
      { section: 8, phrase: 'best health insurance' },
      { section: 10, phrase: 'Comparing health insurance' },
    ],
  },
};

// Text inside these is already a link (or not prose) and must not be wrapped.
const SKIP_PARENTS = new Set([
  'hyperlink',
  'entry-hyperlink',
  'asset-hyperlink',
  'embedded-entry-inline',
]);

/**
 * Returns a copy of a Contentful rich-text document with the first
 * occurrence of `phrase` linked to `uri`. Marks on the original text node
 * are kept on all three resulting pieces. Returns { doc, linked }.
 */
export const linkFirstPhrase = (doc, phrase, uri) => {
  let linked = false;

  const visit = (node) => {
    if (linked || !node || !Array.isArray(node.content)) return node;
    if (SKIP_PARENTS.has(node.nodeType)) return node;

    const content = [];
    node.content.forEach((child) => {
      if (linked) {
        content.push(child);
        return;
      }
      if (child.nodeType === 'text') {
        const at = child.value.indexOf(phrase);
        if (at === -1) {
          content.push(child);
          return;
        }
        const marks = child.marks || [];
        const before = child.value.slice(0, at);
        const after = child.value.slice(at + phrase.length);
        if (before) content.push({ ...child, value: before });
        content.push({
          nodeType: 'hyperlink',
          data: { uri },
          content: [{ nodeType: 'text', value: phrase, marks, data: {} }],
        });
        if (after) content.push({ ...child, value: after });
        linked = true;
        return;
      }
      content.push(visit(child));
    });
    return { ...node, content };
  };

  const out = visit(doc);
  return { doc: out, linked };
};

/**
 * Applies the configured links for one section of one wiki page.
 * `raw` is the Contentful rich-text JSON string; the parsed (and possibly
 * linked) document is returned.
 */
export const applyWikiHubLinks = (slug, section, raw) => {
  const doc = JSON.parse(raw);
  const config = WIKI_HUB_LINKS[slug];
  if (!config) return doc;
  return config.phrases
    .filter((p) => p.section === section)
    .reduce((acc, p) => linkFirstPhrase(acc, p.phrase, config.hub).doc, doc);
};
